import { createPyodideWorker, disposeWorker } from "../workers/PyodideWorker";

import { PyodideInterface } from "pyodide";
import { filesystemService } from "../utils/filesystem";
import { projectService } from "./ProjectService";

interface ExecutionResult {
  success: boolean;
  components?: any;
  error?: string;
}

export enum PyodideState {
  UNINITIALIZED = "uninitialized",
  LOADING = "loading",
  READY = "ready",
  ERROR = "error",
}

// Service to manage Pyodide instance and script execution
export class PyodideService {
  private static instance: PyodideService;
  private worker: Worker | null = null;
  private pyodide: PyodideInterface | null = null;
  private isInitialized: boolean = false;
  private activeScriptPath: string | null = null;
  private initializePromise: Promise<boolean> | null = null;
  private _loadingState: PyodideState = PyodideState.UNINITIALIZED;

  // Callbacks for worker messages
  private messageCallbacks: {
    [key: string]: Array<(data: any) => void>;
  } = {};

  private constructor() { }

  // Get the singleton instance
  public static getInstance(): PyodideService {
    if (!PyodideService.instance) {
      PyodideService.instance = new PyodideService();
    }
    return PyodideService.instance;
  }

  public get loadingState(): PyodideState {
    return this._loadingState;
  }

  // Check if Pyodide is initialized
  public isReady(): boolean {
    return this.isInitialized && this.worker !== null;
  }

  public hasWorker(): boolean {
    return this.worker !== null;
  }

  // Get the active script path
  public getActiveScriptPath(): string | null {
    return this.activeScriptPath;
  }
  // Subscribe to worker messages
  private subscribeToMessage(
    type: string,
    callback: (data: any) => void
  ): () => void {
    if (!this.messageCallbacks[type]) {
      this.messageCallbacks[type] = [];
    }
    this.messageCallbacks[type].push(callback);

    // Return unsubscribe function
    return () => {
      this.messageCallbacks[type] = this.messageCallbacks[type].filter(
        (cb) => cb !== callback
      );
    };
  }

  // Handle messages from the worker
  private handleWorkerMessage(event: MessageEvent): void {
    const { type, ...data } = event.data;

    // Call all registered callbacks for this message type
    if (this.messageCallbacks[type]) {
      this.messageCallbacks[type].forEach((callback) => callback(data));
    }

    // Handle specific message types that affect service state
    switch (type) {
      case "READY":
        this.isInitialized = true;
        this._loadingState = PyodideState.READY;
        break;

      case "ERROR":
        console.error("Pyodide worker error:", data.error);
        this._loadingState = PyodideState.ERROR;
        break;

      case "EXECUTION_RESULT":
        this.activeScriptPath = data.scriptPath || this.activeScriptPath;
        break;

      case "LOADING_STATUS":
        if (data.status === "ready" || data.status === "error") {
          console.log("Pyodide loading status:", data.status);
        }
        break;
    }
  }

  public subscribeToWorkerMessage(
    type: string,
    callback: (data: any) => void
  ): () => void {
    return this.subscribeToMessage(type, callback);
  }

  // Initialize Pyodide via web worker
  public async initialize(): Promise<boolean> {
    // If already initialized, return immediately
    if (this.isInitialized && this.worker) {
      return true;
    }

    // If initialization is in progress, return the existing promise
    if (this.initializePromise) {
      return this.initializePromise;
    }

    // Set state to loading
    this._loadingState = PyodideState.LOADING;

    // Create a promise to track initialization
    this.initializePromise = new Promise<boolean>((resolve) => {
      try {
        console.log("Creating Pyodide Web Worker");
        // Create the web worker
        this.worker = createPyodideWorker();

        // Setup message handling
        this.worker.onmessage = (event) => this.handleWorkerMessage(event);

        // Wait for the READY message
        const unsubscribe = this.subscribeToMessage("READY", async () => {
          this.isInitialized = true;
          this._loadingState = PyodideState.READY;
          console.log("READY received by PyodideService");

          // Sync any pending files from filesystem service
          await filesystemService.handlePyodideReady();

          unsubscribe(); // Remove this listener
          resolve(true);
        });

        // Also handle initialization errors
        const errorUnsubscribe = this.subscribeToMessage("ERROR", (data) => {
          console.error("Pyodide initialization error:", data.error);
          errorUnsubscribe();
          if (!this.isInitialized) {
            this._loadingState = PyodideState.ERROR;
            this.initializePromise = null;
            resolve(false);
          }
        });

        // Tell the worker to initialize
        this.worker.postMessage({ type: "INITIALIZE" });

        // Notify filesystem service that we're now using a worker
        filesystemService.setWorkerMode(true);
      } catch (error) {
        console.error("Error creating Pyodide worker:", error);
        this._loadingState = PyodideState.ERROR;
        this.initializePromise = null;
        resolve(false);
      }
    });

    return this.initializePromise;
  }

  // Load files into Pyodide's filesystem
  public async loadFiles(
    files: Record<string, string | ArrayBuffer>
  ): Promise<boolean> {
    if (!this.worker || !this.isInitialized) {
      await this.initialize();
    }

    return new Promise<boolean>((resolve) => {
      // Listen for completion or error
      const unsubscribe = this.subscribeToMessage("FILES_LOADED", () => {
        unsubscribe();
        errorUnsubscribe();
        resolve(true);
      });

      const errorUnsubscribe = this.subscribeToMessage("ERROR", (data) => {
        console.error("Error loading files:", data.error);
        unsubscribe();
        errorUnsubscribe();
        resolve(false);
      });

      // Send the files to the worker
      this.worker!.postMessage({
        type: "LOAD_FILES",
        data: { files },
      });
    });
  }

  // Start a Python script execution
  public async startScript(scriptPath: string): Promise<ExecutionResult> {
    if (!this.isReady()) {
      try {
        const initialized = await this.initialize();
        if (!initialized) {
          return {
            success: false,
            error: "Failed to initialize Pyodide worker",
          };
        }
      } catch (error) {
        return {
          success: false,
          error: `Failed to initialize Pyodide: ${error}`,
        };
      }
    }

    return new Promise<ExecutionResult>((resolve) => {
      // Listen for execution result
      const resultUnsubscribe = this.subscribeToMessage(
        "EXECUTION_RESULT",
        (data) => {
          resultUnsubscribe();
          errorUnsubscribe();
          this.activeScriptPath = scriptPath;
          resolve({
            success: true,
            components: data.components,
          });
        }
      );

      // Listen for errors
      const errorUnsubscribe = this.subscribeToMessage("ERROR", (data) => {
        resultUnsubscribe();
        errorUnsubscribe();
        resolve({
          success: false,
          error: data.error,
        });
      });

      // Run the script
      this.worker!.postMessage({
        type: "RUN_SCRIPT",
        data: { scriptPath },
      });
    });
  }

  // Stop the currently running script
  public async stopScript(): Promise<boolean> {
    if (!this.activeScriptPath || !this.worker) {
      return true; // No script running
    }

    this.activeScriptPath = null;

    return true; // No explicit stop needed, just clear the reference
  }

  // Update a component value (for interactive components)
  public async updateComponent(
    componentId: string,
    value: any
  ): Promise<boolean> {
    if (!this.isReady() || !this.activeScriptPath) {
      return false;
    }

    return new Promise<boolean>((resolve) => {
      // Listen for component update
      const updateUnsubscribe = this.subscribeToMessage(
        "COMPONENT_UPDATE",
        () => {
          updateUnsubscribe();
          errorUnsubscribe();
          resolve(true);
        }
      );

      // Listen for errors
      const errorUnsubscribe = this.subscribeToMessage("ERROR", (data) => {
        console.error("Error updating component:", data.error);
        updateUnsubscribe();
        errorUnsubscribe();
        resolve(false);
      });

      // Send the update
      this.worker!.postMessage({
        type: "UPDATE_COMPONENT",
        data: { componentId, value },
      });
    });
  }

  // Get a snapshot of the filesystem
  public async getFilesystemSnapshot(): Promise<any> {
    if (!this.isReady()) {
      return null;
    }

    return new Promise<any>((resolve) => {
      // Listen for snapshot
      const snapshotUnsubscribe = this.subscribeToMessage(
        "FS_SNAPSHOT",
        (data) => {
          snapshotUnsubscribe();
          errorUnsubscribe();
          resolve(data.snapshot);
        }
      );

      // Listen for errors
      const errorUnsubscribe = this.subscribeToMessage("ERROR", (data) => {
        console.error("Error getting filesystem snapshot:", data.error);
        snapshotUnsubscribe();
        errorUnsubscribe();
        resolve(null);
      });

      // Request the snapshot
      this.worker!.postMessage({ type: "SERIALIZE_FS" });
    });
  }

  public async getBrandingInfo(): Promise<any> {
    if (!this.worker || !this.isInitialized) {
      await this.initialize();
    }

    return new Promise<any>((resolve) => {
      // Listen for branding info response
      const unsubscribe = this.subscribeToMessage("BRANDING_INFO", (data) => {
        unsubscribe();
        errorUnsubscribe();
        resolve(data.data || {});
      });

      // Listen for errors
      const errorUnsubscribe = this.subscribeToMessage("ERROR", (data) => {
        console.error("Error getting branding info:", data.error);
        unsubscribe();
        errorUnsubscribe();
        resolve({}); // Return empty object on error
      });

      // Request the branding info
      this.worker!.postMessage({ type: "GET_BRANDING" });
    });
  }

  public async createNewProject(templateId?: string): Promise<boolean> {
    try {
      const apiBaseUrl = import.meta.env.VITE_BACKEND_URL || "";
      const effectiveTemplateId = templateId || 'blank';

      const response = await fetch(`${apiBaseUrl}/api/templates/project/${effectiveTemplateId}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch project templates: ${response.statusText}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(`Failed to get project templates: ${result.error}`);
      }

      const files: Record<string, string | ArrayBuffer> = {};
      const templateFiles = result.data.files;

      for (const [path, content] of Object.entries(templateFiles)) {
        if (path.endsWith('_type')) {
          continue;
        }

        const typeKey = `${path}_type`;
        if (templateFiles[typeKey] === 'binary') {
          const binaryString = atob(content as string);
          const arrayBuffer = new ArrayBuffer(binaryString.length);
          const uint8Array = new Uint8Array(arrayBuffer);
          for (let i = 0; i < binaryString.length; i++) {
            uint8Array[i] = binaryString.charCodeAt(i);
          }
          files[path] = arrayBuffer;
        } else {
          files[path] = content as string;
        }
      }

      // Load all files into the worker
      if (this.isReady()) {
        const success = await this.loadFiles(files);
        if (!success) {
          throw new Error("Failed to load project files into Pyodide");
        }
      } else {
        for (const [path, content] of Object.entries(files)) {
          filesystemService.pendingFiles[path] = content;
        }
      }

      console.log("All project files have been created successfully.");
      return true;
    } catch (error) {
      console.error("Error creating new project:", error);
      return false;
    }
  }

  // Helper functions
  extractProjectSlugFromConfig(configContent: string): string {
    // Simple parsing for project slug from TOML
    const match = configContent.match(
      /\[project\][\s\S]*?slug\s*=\s*["']([^"']*)["']/
    );
    return match ? match[1] : "preswald-app";
  }

  extractUrlFromMessage(message: string): string {
    // Extract URL from domain assignment message
    const match = message.match(/Custom domain assigned at ([^\s]+)/);
    return match ? `https://${match[1]}` : "";
  }

  public async shutdown(): Promise<void> {
    if (this.worker) {
      // Stop any running script
      await this.stopScript();

      // Tell the worker to shut down
      this.worker.postMessage({ type: "SHUTDOWN" });

      // Reset state
      this.worker = null;
      this.isInitialized = false;
      this.initializePromise = null;
      this._loadingState = PyodideState.UNINITIALIZED;

      disposeWorker();
    }
  }

  public async getPyodide(): Promise<PyodideInterface> {
    if (!this.isReady()) {
      throw new Error("Pyodide is not ready");
    }
    return this.pyodide!;
  }

  public async generateAndApplyCode(
    scriptPath: string,
    generatedCode: string
  ): Promise<{ success: boolean; content?: string; error?: string }> {
    if (!this.isReady()) {
      try {
        const initialized = await this.initialize();
        if (!initialized) {
          return {
            success: false,
            error: "Failed to initialize Pyodide worker for code generation",
          };
        }
      } catch (error) {
        return {
          success: false,
          error: `Failed to initialize Pyodide: ${error}`,
        };
      }
    }

    return new Promise((resolve) => {
      const resultUnsubscribe = this.subscribeToMessage(
        "CODE_GENERATION_RESULT",
        (data) => {
          resultUnsubscribe();
          errorUnsubscribe();
          resolve({
            success: true,
            content: data.content,
          });
        }
      );

      const errorUnsubscribe = this.subscribeToMessage("ERROR", (data) => {
        if (data.operation === "generateAndApplyCode") {
          resultUnsubscribe();
          errorUnsubscribe();
          resolve({
            success: false,
            error: data.error,
          });
        }
      });

      this.worker!.postMessage({
        type: "GENERATE_AND_APPLY_CODE",
        data: { scriptPath, generatedCode },
      });
    });
  }

  public async listFilesInDirectory(directory: string): Promise<string[]> {
    if (!this.isReady()) {
      try {
        const initialized = await this.initialize();
        if (!initialized) {
          throw new Error(
            "Failed to initialize Pyodide worker for directory listing"
          );
        }
      } catch (error) {
        throw new Error(`Failed to initialize Pyodide: ${error}`);
      }
    }

    return new Promise((resolve, reject) => {
      const resultUnsubscribe = this.subscribeToMessage(
        "DIRECTORY_LISTING",
        (data) => {
          if (data.directory === directory) {
            resultUnsubscribe();
            errorUnsubscribe();
            resolve(data.files || []);
          }
        }
      );

      const errorUnsubscribe = this.subscribeToMessage("ERROR", (data) => {
        if (data.operation === "listFilesInDirectory") {
          resultUnsubscribe();
          errorUnsubscribe();
          reject(new Error(data.error || "Unknown error listing directory"));
        }
      });

      this.worker!.postMessage({
        type: "LIST_DIRECTORY",
        data: { directory },
      });
    });
  }

  public async clearFilesystem(): Promise<boolean> {
    filesystemService.clearPendingFiles();

    if (!this.worker || !this.isInitialized) {
      return true;
    }

    return new Promise<boolean>((resolve) => {
      const unsubscribe = this.subscribeToMessage("FILESYSTEM_CLEARED", () => {
        unsubscribe();
        errorUnsubscribe();
        resolve(true);
      });

      const errorUnsubscribe = this.subscribeToMessage("ERROR", (data) => {
        console.error("Error clearing filesystem:", data.error);
        unsubscribe();
        errorUnsubscribe();
        resolve(false);
      });

      this.worker!.postMessage({
        type: "CLEAR_FILESYSTEM",
      });
    });
  }

  public async exportHtml(scriptPath?: string, client_type: string = "comlink"): Promise<{ success: boolean; files?: Record<string, Uint8Array>; message?: string; error?: string }> {
    if (!this.isReady()) {
      try {
        const initialized = await this.initialize();
        if (!initialized) {
          return {
            success: false,
            error: "Failed to initialize Pyodide worker for HTML export",
          };
        }
      } catch (error) {
        return {
          success: false,
          error: `Failed to initialize Pyodide: ${error}`,
        };
      }
    }

    if (!this.activeScriptPath && !scriptPath) {
      return {
        success: false,
        error: "No active script path provided for export.",
      };
    }

    return new Promise((resolve) => {
      const resultUnsubscribe = this.subscribeToMessage(
        "HTML_EXPORT_RESULT",
        (data) => {
          resultUnsubscribe();
          errorUnsubscribe();
          resolve({
            success: true,
            files: data.files,
            message: data.message,
          });
        }
      );

      const errorUnsubscribe = this.subscribeToMessage("ERROR", (data) => {
        if (data.operation === "exportHtml") {
          resultUnsubscribe();
          errorUnsubscribe();
          resolve({
            success: false,
            error: data.error,
          });
        }
      });

      this.worker!.postMessage({
        type: "EXPORT_HTML",
        data: { scriptPath: scriptPath || this.activeScriptPath, client_type: client_type },
      });
    });
  }
}

// Export a singleton instance
export const pyodideService = PyodideService.getInstance();

// Set up the global functions that interface with the worker
if (typeof window !== "undefined") {
  window.preswaldRunScript = async (path: string) => {
    const result = await pyodideService.startScript(path);
    return {
      toJs: () => ({
        success: result.success,
        components: result.components,
        error: result.error,
      }),
    };
  };

  window.preswaldUpdateComponent = async (id: string, value: any) => {
    const success = await pyodideService.updateComponent(id, value);
    return {
      toJs: () => ({ success }),
    };
  };

  window.preswaldInit = async (path?: string) => {
    if (!path) {
      return {
        toJs: () => ({
          error: "No path provided",
        }),
      };
    }
    const result = await pyodideService.startScript(path);
    return {
      toJs: () => ({
        success: result.success,
        components: result.components,
        error: result.error,
      }),
    };
  };

  window.preswaldShutdown = async () => {
    await pyodideService.shutdown();
    return {
      toJs: () => ({ success: true }),
    };
  };

  window.preswaldExportHtml = async (path: string, client_type: string) => {
    const result = await pyodideService.exportHtml(path, client_type);
    return {
      toJs: (options?: any) => ({
        success: result.success,
        files: result.files,
        message: result.message,
        error: result.error,
      }),
      destroy: () => {
        // Cleanup function for PyProxy compatibility
      },
    };
  };

  // Alias for backwards/forwards compatibility with new preswaldHTMLExport name
  window.preswaldHTMLExport = window.preswaldExportHtml;
}

